import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Typography,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import CodeIcon from '@mui/icons-material/Code';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';

interface AlgorithmsModalProps {
  open: boolean;
  onClose: () => void;
  includeSflaGaBaseline?: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface ModalTab {
  name: string;
  key: string;
  type: 'algorithm' | 'comparison';
}

interface ScientificComparisonRow {
  algorithm: string;
  family: string;
  structure: string;
  exploration: string;
  exploitation: string;
  convergence: string;
  quality: string;
  scientificNote: string;
  bestUse: string;
}

interface EmpiricalRankingRow {
  criterion: string;
  ranking: string;
  scope: string;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
    </div>
  );
}

// Theory flowcharts for algorithm families and project-specific hybrids.
const algorithmDiagrams: Record<string, string> = {
  'Genetic Algorithm': `                    START
                     │
                     ▼
        ┌────────────────────────┐
        │ Initialize Population  │
        │ - Random chromosomes   │
        └────────────────────────┘
                     │
                     ▼
        ┌────────────────────────┐
        │ Evaluate Population     │
        │ - Compute fitness       │
        └────────────────────────┘
                     │
                     ▼
        ┌────────────────────────┐
        │ Termination Reached?   │
        └────────────────────────┘
              │
         ┌────┴────┐
        YES        NO
         │          │
         ▼          ▼
┌───────────────┐  ┌────────────────────────┐
│ Return Best   │  │ Select Parents         │
└───────────────┘  │ - According to GA      │
                   │   selection strategy   │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ Apply Crossover        │
                   │ - Recombine parents    │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ Apply Mutation         │
                   │ - Perturb offspring    │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ Evaluate Offspring     │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ Form Next Population   │
                   │ - Replacement / elites │
                   └────────────────────────┘
                                │
                                └───────────────↺`,

  'Cultural Algorithm': `                    START
                     │
                     ▼
        ┌────────────────────────┐
        │ Initialize Population   │
        │ and Belief Space        │
        └────────────────────────┘
                     │
                     ▼
        ┌────────────────────────┐
        │ Evaluate Population     │
        └────────────────────────┘
                     │
                     ▼
        ┌────────────────────────┐
        │ Termination Reached?   │
        └────────────────────────┘
              │
         ┌────┴────┐
        YES        NO
         │          │
         ▼          ▼
┌───────────────┐  ┌────────────────────────┐
│ Return Best   │  │ Select Accepted Set    │
└───────────────┘  │ - Influential members  │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ Update Belief Space    │
                   │ - Situational /        │
                   │   normative knowledge  │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ Influence Population   │
                   │ - Selection /          │
                   │   crossover / mutation │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ Evaluate New Solutions │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ Form Updated           │
                   │ Population             │
                   └────────────────────────┘
                                │
                                └───────────────↺`,

  'SFLA': `                    START
                     │
                     ▼
        ┌────────────────────────┐
        │ Initialize Population  │
        │ (Frogs)                │
        └────────────────────────┘
                     │
                     ▼
        ┌────────────────────────┐
        │ Evaluate and Rank       │
        │ Population              │
        └────────────────────────┘
                     │
                     ▼
        ┌────────────────────────┐
        │ Partition Frogs into    │
        │ Memeplexes              │
        └────────────────────────┘
                     │
                     ▼
        ┌────────────────────────┐
        │ Termination Reached?   │
        └────────────────────────┘
              │
         ┌────┴────┐
        YES        NO
         │          │
         ▼          ▼
┌───────────────┐  ┌────────────────────────┐
│ Return Best   │  │ For Each Memeplex      │
└───────────────┘  │ - Identify best /      │
                   │   worst frogs          │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ Improve Worst Frog     │
                   │ - Toward local best    │
                   │ - Else toward global   │
                   │   best                 │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ Replace or Randomly    │
                   │ Reinitialize Worst     │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ Shuffle Frogs Across   │
                   │ Memeplexes             │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ Re-rank and Reform     │
                   │ Memeplexes             │
                   └────────────────────────┘
                                │
                                └───────────────↺`,

  'SFLA-GA': `                  START
                     │
                     ▼
        ┌────────────────────────┐
        │ Initialize Population  │
        │ and Evaluate Frogs     │
        └────────────────────────┘
                     │
                     ▼
        ┌────────────────────────┐
        │ Rank Population and    │
        │ Form Memeplexes        │
        └────────────────────────┘
                     │
                     ▼
        ┌────────────────────────┐
        │ Termination Reached?   │
        └────────────────────────┘
              │
         ┌────┴────┐
        YES        NO
         │          │
         ▼          ▼
┌───────────────┐  ┌────────────────────────┐
│ Return Best   │  │ For Each Memeplex      │
└───────────────┘  │ - Identify local best  │
                   │   and worst frog       │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ Improve Worst Frog     │
                   │ - Toward local best    │
                   │ - Else global best     │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ Apply GA Operators     │
                   │ - Selection            │
                   │ - Crossover            │
                   │ - Mutation             │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ Evaluate Children and  │
                   │ Replace Weaker Frogs   │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ Shuffle Frogs and      │
                   │ Reform Memeplexes      │
                   └────────────────────────┘
                                │
                                └───────────────↺`,

  'SFLAGA': `                    START
                     │
                     ▼
        ┌────────────────────────┐
        │ Initialize Population  │
        │ and Evaluate Frogs      │
        └────────────────────────┘
                     │
                     ▼
        ┌────────────────────────┐
        │ Rank Population and     │
        │ Form Memeplexes         │
        └────────────────────────┘
                     │
                     ▼
        ┌────────────────────────┐
        │ Termination Reached?   │
        └────────────────────────┘
              │
         ┌────┴────┐
        YES        NO
         │          │
         ▼          ▼
┌───────────────┐  ┌────────────────────────┐
│ Return Best   │  │ For Each Memeplex      │
└───────────────┘  │ - Identify local best  │
                   │   and weak members     │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ SFLA Local Improvement │
                   │ - Improve weakest frog │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ GA Search Within       │
                   │ Memeplex               │
                   │ - Selection            │
                   │ - Crossover            │
                   │ - Mutation             │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ Evaluate and Replace   │
                   │ Weaker Frogs           │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ Shuffle and Reform     │
                   │ Memeplexes             │
                   └────────────────────────┘
                                │
                                └───────────────↺`,

  'PSO': `                    START
                     │
                     ▼
        ┌────────────────────────┐
        │ Initialize Swarm        │
        │ - Positions            │
        │ - Velocities           │
        │ - Personal bests       │
        └────────────────────────┘
                     │
                     ▼
        ┌────────────────────────┐
        │ Evaluate Swarm          │
        │ Find Global Best        │
        └────────────────────────┘
                     │
                     ▼
        ┌────────────────────────┐
        │ Termination Reached?   │
        └────────────────────────┘
              │
         ┌────┴────┐
        YES        NO
         │          │
         ▼          ▼
┌───────────────┐  ┌────────────────────────┐
│ Return Best   │  │ Update Parameters      │
└───────────────┘  │ - Inertia /            │
                   │   acceleration terms   │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ For Each Particle      │
                   │ - Update velocity      │
                   │ - Update position      │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ Evaluate Particle      │
                   │ and Update pBest       │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ Update Global Best     │
                   └────────────────────────┘
                                │
                                └───────────────↺`,

  'GAPSO': `                    START
                     │
                     ▼
        ┌────────────────────────┐
        │ Initialize Swarm        │
        │ Evaluate Swarm          │
        │ Find Global Best        │
        └────────────────────────┘
                     │
                     ▼
        ┌────────────────────────┐
        │ Termination Reached?   │
        └────────────────────────┘
              │
         ┌────┴────┐
        YES        NO
         │          │
         ▼          ▼
┌───────────────┐  ┌────────────────────────┐
│ Return Best   │  │ PSO Phase              │
└───────────────┘  │ - Update velocities    │
                   │ - Update positions     │
                   │ - Update pBest         │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ GA Phase               │
                   │ - Select particles     │
                   │ - Recombine            │
                   │ - Generate offspring   │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ Evaluate and Replace   │
                   │ Improved Particles     │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ Update Global Best     │
                   └────────────────────────┘
                                │
                                └───────────────↺`,

  'Kurd SFLA': `                    START
                     │
                     ▼
        ┌────────────────────────┐
        │ Initialize Population  │
        │ Evaluate Frogs         │
        └────────────────────────┘
                     │
                     ▼
        ┌────────────────────────┐
        │ Create Memeplexes,      │
        │ Archive, and Controls   │
        └────────────────────────┘
                     │
                     ▼
        ┌────────────────────────┐
        │ Termination Reached?   │
        └────────────────────────┘
              │
         ┌────┴────┐
        YES        NO
         │          │
         ▼          ▼
┌───────────────┐  ┌────────────────────────┐
│ Return Best   │  │ Compute Adaptive       │
└───────────────┘  │ Search Intensity       │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ For Each Memeplex      │
                   │ - Assign search role   │
                   │ - Improve weak frogs   │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ Archive Guidance       │
                   │ and Block Injection    │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ Unity Shuffle and      │
                   │ Elite Migration        │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ Controlled Renewal if  │
                   │ Stagnation Occurs      │
                   └────────────────────────┘
                                │
                                ▼
                   ┌────────────────────────┐
                   │ Reform Memeplexes      │
                   │ for Next Round         │
                   └────────────────────────┘
                                │
                                └───────────────↺`,
};

const algorithmSteps: Record<string, string[]> = {
  'Genetic Algorithm': [
    'Initialize a random population of chromosomes.',
    'Evaluate the initial population before evolution starts.',
    'At each generation, sort the population by fitness and copy the top 10% as elites.',
    'Fill the rest of the next generation by selecting parents with tournament selection or random selection, depending on `selectionRate`.',
    'Apply single-point crossover with probability `crossoverRate`; otherwise copy the parents directly.',
    'Mutate each child independently with probability `mutationRate`.',
    'Evaluate each child once after crossover and mutation are complete.',
    'Replace the old population with the newly built generation.',
    'Update progress with the best chromosome and repeat until the generation limit is reached.',
  ],
  'Cultural Algorithm': [
    'Initialize and evaluate the population, then initialize an explicit belief space with situational and normative knowledge containers.',
    'At each generation, sort the population and take the top accepted set to update the belief space.',
    'Set the best accepted chromosome as situational knowledge and compute the most frequent service choice per task as normative knowledge.',
    'Copy the elite subset directly into the next generation.',
    'Fill the rest of the next generation by selecting parents with the shared `selectionRate` policy and applying crossover when the crossover test succeeds.',
    'Influence offspring with belief-space guidance before mutation, using situational and normative knowledge to adjust selected tasks.',
    'Apply mutation with probability `mutationRate`, evaluate offspring, and build the next generation generationally.',
    'Report the current best chromosome and repeat until the generation limit is reached.',
  ],
  'SFLA': [
    'Initialize the frog population and evaluate all frogs.',
    'Sort the population globally and divide it into memeplexes with round-robin distribution.',
    'At each generation, find the current global best frog.',
    'Inside each memeplex, sort frogs from best to worst.',
    'Try to improve the worst frog by copying a bounded set of genes from the memeplex best.',
    'If that fails, try the same update toward the global best frog.',
    'If both attempts fail, replace the worst frog with a new random solution.',
    'After all memeplexes are processed, shuffle frogs back into the population.',
    'Sort the population again and recreate memeplexes for the next generation.',
    'Report the best frog and repeat until the generation limit is reached.',
  ],
  'SFLA-GA': [
    'Initialize the frog population, evaluate all chromosomes, and sort the population by fitness.',
    'Distribute frogs into memeplexes with round-robin assignment.',
    'At each generation, identify the current global best frog.',
    'Within each memeplex, improve the worst frog by guided copying from the local best and, if needed, the global best.',
    'If guided refinement does not help, keep the stronger candidate instead of injecting undocumented heuristics.',
    'Select parent frogs with the shared selection-rate policy and apply crossover inside the memeplex.',
    'Mutate offspring with the configured mutation rate, evaluate them, and replace weaker frogs only when offspring improve fitness.',
    'Shuffle memeplexes back into the global population, re-sort frogs, and reform memeplexes for the next generation.',
    'Report the best chromosome and repeat until the generation limit is reached.',
  ],
  'SFLAGA': [
    'Initialize and evaluate the population, sort it by fitness, and create memeplexes.',
    'At each generation, preserve the current global best chromosome before evolving memeplexes.',
    'Within each memeplex, sort frogs and protect the top elite subset from replacement.',
    'Improve the worst frog with multiple local-search attempts using the local best, global best, and elite best guides.',
    'If no guided attempt improves the worst frog, replace it with a random chromosome to maintain exploration.',
    'Generate offspring inside each memeplex with higher-quality parent selection that can draw from elites or the global best depending on generation progress.',
    'Apply mutation only when it improves the child, so mutation acts as local refinement rather than blind disruption.',
    'Use greedy survivor selection to replace non-elite weak frogs when children improve the target or raise the memeplex average.',
    'Shuffle memeplexes back into the global population, restore the preserved global best if needed, then re-sort and reform memeplexes.',
    'Report the best chromosome and repeat until the generation limit is reached.',
  ],
  'PSO': [
    'Initialize the swarm with random positions, zero velocities, and initial personal bests.',
    'Evaluate the initial swarm and set each particle\'s personal-best fitness.',
    'Find the initial global best particle.',
    'At each generation, compute the linearly decreasing inertia weight.',
    'For each particle, update the conceptual velocity with inertia, cognitive, and social terms, then clamp it.',
    'Convert the velocity to a sigmoid transfer probability and use it to decide whether the particle moves toward its personal best or the global best.',
    'Apply a small random exploration fallback when the transfer test does not move the particle.',
    'Evaluate the updated particle.',
    'Update the personal best when the new fitness is better, then refresh the swarm-wide global best after the swarm pass.',
    'Report the best particle and repeat until the generation limit is reached.',
  ],
  'GAPSO': [
    'Initialize the swarm, evaluate all particles, and record the initial global best.',
    'At each generation, compute the inertia weight for the PSO phase.',
    'Run the PSO update over the whole swarm: update velocities, update positions, re-evaluate particles, and refresh personal bests.',
    'Enter the GA hybrid phase and select parent particles with the shared `selectionRate` policy.',
    'Apply crossover to parent positions when the crossover-probability test succeeds.',
    'Convert crossover children into new particles, evaluate them, and inherit or update their personal-best memory.',
    'Replace parent particles only when the corresponding child is fitter.',
    'Refresh the global best, report progress, and repeat until the generation limit is reached.',
  ],
  'Kurd SFLA': [
    'Initialize the frog population and evaluate the starting solutions.',
    'Initialize the pattern archive, create memeplexes, and start recording best-fitness history.',
    'At each generation, find the current global best frog and update the fitness history window.',
    'Calculate population diversity and derive adaptive step-size and mutation-strength multipliers.',
    'Assign each memeplex a rotating role such as QoS refinement, energy-cost refinement, explore-diversify, or balanced search.',
    'Within each memeplex, run resilient local improvement on the worst frog across several trials, using local-best copying, global-best copying, or random changes according to the role.',
    'Optionally inject archive blocks into candidate frogs and apply role-scaled mutation before evaluating them.',
    'Accept the best improving candidate for the worst frog, or partially restart the worst frog when no candidate helps.',
    'Periodically perform unity-guided shuffle with elite migration into weak memeplexes, and update the pattern archive from top frogs.',
    'When stagnation and low diversity occur together, perform controlled renewal on the bottom fraction of the population, optionally seeded by archive blocks.',
    'Shuffle frogs back into the population, recreate memeplexes, report the best frog, and repeat until the generation limit is reached.',
  ],
};

const scientificMethodology = [
  'All baseline algorithms are compared on the same QoS-aware service composition problem with identical dataset structure and consistent parameter conventions.',
  'Energy consumption is the primary optimization objective, while fitness, cost, response time, and execution time are secondary comparison metrics.',
  'For stochastic algorithms, median across repeated runs is the preferred comparison statistic because it is more robust to outliers than mean.',
  'For the discrete service-selection search space, the swarm methods are implemented as discrete/categorical adaptations rather than literal continuous PSO.',
  'Scientific comparison should separate algorithm structure from empirical results, because rankings can shift with task count, search budget, and hybridization overhead.',
];

const scientificTakeaways = [
  'The current benchmark set includes both a standard SFLA-GA baseline and a separate proposed SFLAGA variant.',
  'SFLAGA adds elite preservation and stronger memeplex-level refinement on top of the standard SFLA-GA backbone.',
  'PSO and Cultural are structurally lighter, while the hybrids trade additional overhead for added guidance or recombination power.',
];

const scopeNotes = [
  'The seven-algorithm thesis comparison covers GA, Cultural, SFLA, SFLA-GA, SFLAGA, PSO, and GAPSO.',
  'Kurd SFLA appears in this UI as an extended experimental variant, but it is not part of the same documented baseline benchmark set.',
  'Where the evidence for Kurd SFLA is narrower, the comparison below labels it as experimental rather than giving it unsupported absolute ranks.',
];

const scientificComparisonRows: ScientificComparisonRow[] = [
  {
    algorithm: 'Genetic Algorithm',
    family: 'Evolutionary search',
    structure: 'Flat chromosome population with crossover and mutation',
    exploration: 'High',
    exploitation: 'Medium',
    convergence: 'Medium',
    quality: 'High on broad search spaces',
    scientificNote: 'Strong global exploration, but can converge prematurely without extra structure.',
    bestUse: 'Large search spaces when broad exploration matters more than fast convergence.',
  },
  {
    algorithm: 'Cultural Algorithm',
    family: 'Evolutionary + belief-space guidance',
    structure: 'Flat population with explicit situational and normative belief space',
    exploration: 'Medium',
    exploitation: 'High',
    convergence: 'Fast',
    quality: 'Medium to high',
    scientificNote: 'Belief-space guidance improves exploitation and makes the cultural mechanism explicit instead of approximating it with elite bias alone.',
    bestUse: 'Limited compute budgets and cases where strong exploitation is preferred.',
  },
  {
    algorithm: 'SFLA',
    family: 'Memeplex-based metaheuristic',
    structure: 'Population partitioned into memeplexes with local worst-frog improvement',
    exploration: 'High',
    exploitation: 'Low to medium',
    convergence: 'Slow',
    quality: 'Medium',
    scientificNote: 'Good diversification through memeplexes, but weaker exploitation makes it slower and less competitive than the best hybrids.',
    bestUse: 'Difficult search spaces where diversification is more important than runtime.',
  },
  {
    algorithm: 'SFLA-GA',
    family: 'Standard SFLA + GA hybrid',
    structure: 'Memeplex search with guided worst-frog updates plus standard GA selection, crossover, and mutation',
    exploration: 'High',
    exploitation: 'High',
    convergence: 'Medium to fast',
    quality: 'High',
    scientificNote: 'Serves as the defensible baseline hybrid for comparison with the proposed SFLAGA enhancement.',
    bestUse: 'Baseline hybrid benchmarking when the study needs a standard SFLA+GA reference point.',
  },
  {
    algorithm: 'SFLAGA',
    family: 'Enhanced SFLA + GA hybrid',
    structure: 'Memeplexes plus elite preservation, multi-trial local search, and greedy survivor selection',
    exploration: 'High',
    exploitation: 'Very high',
    convergence: 'Fast and stable',
    quality: 'Very high',
    scientificNote: 'Proposed thesis variant that keeps the SFLA+GA backbone but adds explicit enhancement mechanisms instead of claiming to be the plain standard hybrid.',
    bestUse: 'Proposed-method evaluation when stronger exploitation and protected convergence are desired.',
  },
  {
    algorithm: 'PSO',
    family: 'Swarm intelligence',
    structure: 'Particles with velocity, personal best, and global best in a discrete/categorical adaptation',
    exploration: 'Low',
    exploitation: 'High',
    convergence: 'Fastest',
    quality: 'Medium to low',
    scientificNote: 'Implemented as a discrete PSO adaptation using sigmoid-guided movement, which is more defensible for categorical service selection than the earlier heuristic move rule.',
    bestUse: 'Quick approximate results and runtime-sensitive scenarios.',
  },
  {
    algorithm: 'GAPSO',
    family: 'PSO + GA hybrid',
    structure: 'Swarm search followed by GA-style crossover over particles',
    exploration: 'Medium',
    exploitation: 'High',
    convergence: 'Fast',
    quality: 'High',
    scientificNote: 'A pragmatic compromise: faster than pure GA-style search with stronger final quality than pure PSO.',
    bestUse: 'Balanced performance when both speed and final quality matter.',
  },
  {
    algorithm: 'Kurd SFLA',
    family: 'Extended SFLA variant',
    structure: 'Role-based memeplexes with archive, unity shuffle, renewal, and adaptive control',
    exploration: 'Task-size dependent',
    exploitation: 'Task-size dependent',
    convergence: 'Experimental',
    quality: 'Experimental',
    scientificNote: 'The fix report positions it as baseline-like SFLA on small tasks and a more exploratory SFLA on large tasks; its evidence base is narrower than the seven baseline algorithms.',
    bestUse: 'Experimental large-task studies where enhanced SFLA-style exploration is being investigated.',
  },
];

const empiricalRankingRows: EmpiricalRankingRow[] = [
  {
    criterion: 'Comparison protocol',
    ranking: 'Same dataset class, repeated runs, median-first analysis, and energy as the primary decision metric.',
    scope: 'Methodology used in the project comparison documents.',
  },
  {
    criterion: 'Canonical status in current build',
    ranking: 'GA, SFLA, and SFLA-GA are the baseline family references; PSO is a discrete adaptation; SFLAGA and GAPSO are enhanced/project variants.',
    scope: 'Current implementation structure after the fairness and canonicalization cleanup.',
  },
  {
    criterion: 'Runtime interpretation',
    ranking: 'Lighter single-family methods generally have less structural overhead than the hybrids, but the actual ordering must be verified empirically for each task range.',
    scope: 'Interpretation rule, not a fixed current ranking.',
  },
  {
    criterion: 'Quality interpretation',
    ranking: 'Hybrids often improve search guidance or diversification, but quality claims should come from the exported experiment results rather than static documentation.',
    scope: 'Interpretation rule for current and future experiments.',
  },
  {
    criterion: 'Interpretation caution',
    ranking: 'Kurd SFLA remains experimental, and the current modal should not be read as a substitute for benchmark exports when making thesis claims.',
    scope: 'Evidence-strength caution for documentation versus measured results.',
  },
];

const AlgorithmsModal: React.FC<AlgorithmsModalProps> = ({
  open,
  onClose,
  includeSflaGaBaseline = false,
}) => {
  const theme = useTheme();
  const [value, setValue] = useState(0);
  const [isMaximized, setIsMaximized] = useState(false);

  const modalTabs: ModalTab[] = [
    { name: 'Genetic Algorithm', key: 'Genetic Algorithm', type: 'algorithm' },
    { name: 'Cultural Algorithm', key: 'Cultural Algorithm', type: 'algorithm' },
    { name: 'SFLA', key: 'SFLA', type: 'algorithm' },
    ...(includeSflaGaBaseline ? [{ name: 'SFLA-GA', key: 'SFLA-GA', type: 'algorithm' as const }] : []),
    { name: 'SFLAGA', key: 'SFLAGA', type: 'algorithm' },
    { name: 'PSO', key: 'PSO', type: 'algorithm' },
    { name: 'GAPSO', key: 'GAPSO', type: 'algorithm' },
    { name: 'Kurd SFLA', key: 'Kurd SFLA', type: 'algorithm' },
    { name: 'Scientific Comparison', key: 'Scientific Comparison', type: 'comparison' },
  ];
  const visibleScientificTakeaways = includeSflaGaBaseline
    ? scientificTakeaways
    : [
        'The optional standard SFLA-GA baseline is currently excluded from the active comparison set.',
        'SFLAGA remains the enhanced SFLA+GA-style variant in the active build.',
        scientificTakeaways[2],
      ];
  const visibleScopeNotes = includeSflaGaBaseline
    ? scopeNotes
    : [
        'The active thesis comparison covers GA, Cultural, SFLA, SFLAGA, PSO, and GAPSO.',
        scopeNotes[1],
        scopeNotes[2],
      ];
  const visibleScientificComparisonRows = includeSflaGaBaseline
    ? scientificComparisonRows
    : scientificComparisonRows.filter((row) => row.algorithm !== 'SFLA-GA');
  const visibleEmpiricalRankingRows = empiricalRankingRows.map((row) => {
    if (!includeSflaGaBaseline && row.criterion === 'Canonical status in current build') {
      return {
        ...row,
        ranking: 'GA and SFLA are the baseline family references; PSO is a discrete adaptation; SFLAGA and GAPSO are enhanced/project variants.',
      };
    }

    return row;
  });

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleToggleMaximize = () => {
    setIsMaximized((prev) => !prev);
  };

  useEffect(() => {
    if (!open) {
      setIsMaximized(false);
    }
  }, [open]);

  useEffect(() => {
    if (value >= modalTabs.length) {
      setValue(Math.max(0, modalTabs.length - 1));
    }
  }, [modalTabs.length, value]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={isMaximized ? false : 'lg'}
      fullWidth
      PaperProps={{
        sx: {
          display: 'flex',
          flexDirection: 'column',
          width: isMaximized ? '100vw' : undefined,
          maxWidth: isMaximized ? '100vw' : undefined,
          height: isMaximized ? '100vh' : undefined,
          maxHeight: isMaximized ? '100vh' : '90vh',
          borderRadius: isMaximized ? 0 : 2,
          m: isMaximized ? 0 : undefined,
        },
      }}
    >
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CodeIcon sx={{ fontSize: 28 }} />
          <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
            Algorithm Flowcharts
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton
            onClick={handleToggleMaximize}
            aria-label={isMaximized ? 'Restore dialog size' : 'Maximize dialog'}
            sx={{
              color: 'white',
              '&:hover': {
                backgroundColor: alpha('#ffffff', 0.1),
              },
            }}
          >
            {isMaximized ? <CloseFullscreenIcon /> : <OpenInFullIcon />}
          </IconButton>
          <IconButton
            onClick={onClose}
            aria-label="Close dialog"
            sx={{
              color: 'white',
              '&:hover': {
                backgroundColor: alpha('#ffffff', 0.1),
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={value}
            onChange={handleChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                minHeight: 48,
              },
            }}
          >
            {modalTabs.map((tab, index) => (
              <Tab key={index} label={tab.name} />
            ))}
          </Tabs>
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
          {modalTabs.map((tab, index) => (
            <TabPanel key={index} value={value} index={index}>
              {tab.type === 'comparison' ? (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    backgroundColor: alpha(theme.palette.background.default, 0.5),
                  }}
                >
                  <Typography variant="h6" gutterBottom sx={{ mb: 1, fontWeight: 600 }}>
                    Scientific Comparison
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 960 }}>
                    This section separates methodology, structural characteristics, and documented empirical trends so the comparison remains evidence-based rather than purely descriptive.
                  </Typography>

                  <Box
                    sx={{
                      display: 'grid',
                      gap: 2,
                      mb: 3,
                      gridTemplateColumns: {
                        xs: '1fr',
                        md: 'repeat(3, minmax(0, 1fr))',
                      },
                    }}
                  >
                    <Paper variant="outlined" sx={{ p: 2.5, backgroundColor: alpha(theme.palette.primary.main, 0.04) }}>
                      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
                        Methodology
                      </Typography>
                      <Box component="ul" sx={{ m: 0, pl: 2.25, display: 'grid', gap: 1 }}>
                        {scientificMethodology.map((item, itemIndex) => (
                          <Box component="li" key={itemIndex}>
                            <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                              {item}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Paper>

                    <Paper variant="outlined" sx={{ p: 2.5, backgroundColor: alpha(theme.palette.success.main, 0.05) }}>
                      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
                        Main Findings
                      </Typography>
                      <Box component="ul" sx={{ m: 0, pl: 2.25, display: 'grid', gap: 1 }}>
                        {visibleScientificTakeaways.map((item, itemIndex) => (
                          <Box component="li" key={itemIndex}>
                            <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                              {item}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Paper>

                    <Paper variant="outlined" sx={{ p: 2.5, backgroundColor: alpha(theme.palette.warning.main, 0.07) }}>
                      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
                        Scope Notes
                      </Typography>
                      <Box component="ul" sx={{ m: 0, pl: 2.25, display: 'grid', gap: 1 }}>
                        {visibleScopeNotes.map((item, itemIndex) => (
                          <Box component="li" key={itemIndex}>
                            <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                              {item}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Paper>
                  </Box>

                  <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600 }}>
                    Algorithm-by-Algorithm Analysis
                  </Typography>
                  <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{ mb: 3, backgroundColor: theme.palette.background.paper }}
                  >
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>Algorithm</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Family</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Population Structure</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Explore</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Exploit</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Convergence</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Quality</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Scientific Note</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Best Use</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {visibleScientificComparisonRows.map((row) => (
                          <TableRow key={row.algorithm} hover>
                            <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{row.algorithm}</TableCell>
                            <TableCell>{row.family}</TableCell>
                            <TableCell>{row.structure}</TableCell>
                            <TableCell>{row.exploration}</TableCell>
                            <TableCell>{row.exploitation}</TableCell>
                            <TableCell>{row.convergence}</TableCell>
                            <TableCell>{row.quality}</TableCell>
                            <TableCell>{row.scientificNote}</TableCell>
                            <TableCell>{row.bestUse}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600 }}>
                    Documented Empirical Ranking Summary
                  </Typography>
                  <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{ backgroundColor: theme.palette.background.paper }}
                  >
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>Criterion</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Documented Trend</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Evidence Scope</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {visibleEmpiricalRankingRows.map((row) => (
                          <TableRow key={row.criterion} hover>
                            <TableCell sx={{ fontWeight: 600 }}>{row.criterion}</TableCell>
                            <TableCell>{row.ranking}</TableCell>
                            <TableCell>{row.scope}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              ) : (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    backgroundColor: alpha(theme.palette.background.default, 0.5),
                  }}
                >
                  <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
                    {tab.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 920, lineHeight: 1.7 }}>
                    The left panel shows a theory-level flowchart for the algorithm family or hybrid design. The right
                    panel summarizes the main steps of this repo&apos;s current implementation.
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gap: 3,
                      alignItems: 'start',
                      gridTemplateColumns: {
                        xs: '1fr',
                        md: 'minmax(0, 1.65fr) minmax(280px, 0.95fr)',
                      },
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          mb: 1.5,
                          color: theme.palette.text.secondary,
                          textTransform: 'uppercase',
                          letterSpacing: 0.8,
                        }}
                      >
                        Theory Flowchart
                      </Typography>
                      <Box
                        component="pre"
                        sx={{
                          margin: 0,
                          padding: 3,
                          backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#fafafa',
                          borderRadius: 1,
                          overflow: 'auto',
                          fontSize: '0.8125rem',
                          fontFamily: '"Courier New", Courier, "Lucida Console", Monaco, monospace',
                          lineHeight: 1.5,
                          border: `1px solid ${theme.palette.divider}`,
                          whiteSpace: 'pre',
                          color: theme.palette.text.primary,
                        }}
                      >
                        {algorithmDiagrams[tab.key] || `Diagram for ${tab.name} not available.`}
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        minWidth: 0,
                        position: { md: 'sticky' },
                        top: 0,
                        alignSelf: 'start',
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          mb: 1.5,
                          color: theme.palette.text.secondary,
                          textTransform: 'uppercase',
                          letterSpacing: 0.8,
                        }}
                      >
                        Implementation Steps
                      </Typography>
                      <Box
                        sx={{
                          p: 2.5,
                          borderRadius: 1,
                          border: `1px solid ${theme.palette.divider}`,
                          backgroundColor: alpha(theme.palette.primary.main, 0.04),
                        }}
                      >
                        <Box
                          component="ol"
                          sx={{
                            m: 0,
                            pl: 2.75,
                            display: 'grid',
                            gap: 1.25,
                          }}
                        >
                          {(algorithmSteps[tab.key] || []).map((step, stepIndex) => (
                            <Box component="li" key={stepIndex} sx={{ pl: 0.25 }}>
                              <Typography variant="body2" sx={{ lineHeight: 1.65 }}>
                                {step}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              )}
            </TabPanel>
          ))}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AlgorithmsModal;
